/* eslint no-magic-numbers: ["error", { "ignore": [0,1,2] }] */
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { saveAs } from 'file-saver'
import qs from 'query-string'

import { usePagination } from '../../../hooks/usePagination'
import sortDirection from '../../../constants/sortDirection'
import {
  downloadPressuresReportFile,
  getPressuresReportsRequest
} from '../../../api/reports'
import { showErrorToast } from '../../../utils/toast'
import { buildReportsExpandPath } from '../../../routing/constants'
import ExpandedReportsTable from './ExpandedReportsTable'
import {
  makePressuresTabColumns,
  makePressuresTabRow
} from './pressuresTabTable'
import {
  Dash,
  ReportsPaginationWrapper,
  ReportsTableWrapper,
  ReportTableTitle,
  ReportTableWrapper,
  RightButtons
} from '../style'
import Button from '../../../components/Button'
import Download from '../../../assets/icons/download-icon'
import Expand from '../../../assets/icons/expand-icon'
import Table from '../../../components/Table'
import ItemsPerPageDropdown from '../../../components/ItemsPerPageDropdown'
import Pagination from '../../../features/Pagination'
import { getUrlForAllFilters } from '../index'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 10,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const defaultFilters = {
  siteFilters: {
    region: [],
    country: [],
    system: [],
    threats: [],
    assessmentStatus: [],
    kbaStatus: [],
    kbaClassification: [],
    kbaCriterias: [],
    dateOfMostRecentAssessment: [],
    url: ''
  },
  speciesFilters: {
    speciesList: [],
    classList: [],
    selectedSpecieOptions: []
  },
  ecosystemFilters: {
    ecosystemList: [],
    ecosystemRedListCategory: [],
    url: ''
  },
  ecologicalFilters: {
    ecoregionList: [],
    url: ''
  },
  showReports: false
}

const PressuresReportsTab = ({
  filters,
  setFilters,
  setLoadingScreenMessage,
  isPressuresLoading,
  setIsPressuresLoading,
  isRunReportTriggered
}) => {
  const history = useNavigate()
  const params = useParams()
  const {
    pagination,
    setPagination,
    selectPage,
    nextPage,
    prevPage,
    nextSection,
    prevSection,
    changeResultsPerPage
  } = usePagination(defaultPaginationState)
  const [pressuresReports, setPressuresReports] = useState([])
  const [pressuresSorting, setPressuresSorting] = useState({
    sortBy: 'siteCode',
    sortDirection: sortDirection.ASC
  })
  const prevRunReportTriggered = useRef(isRunReportTriggered)

  const getPressuresReports = async () => {
    if (isPressuresLoading) {
      return
    }
    const pressuresPagination = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage
    }
    const sorting = {
      sortBy: pressuresSorting.sortBy,
      sortDirection: pressuresSorting.sortDirection
    }
    setIsPressuresLoading(true)

    try {
      const response = await getPressuresReportsRequest({
        queryStrings: getUrlForAllFilters(filters),
        pagination: qs.stringify(pressuresPagination),
        sorting: qs.stringify(sorting)
      })

      if (response.error) {
        setPressuresReports([])
        setIsPressuresLoading(false)
        setPagination(defaultPaginationState)
        localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
        setFilters(defaultFilters)
        return showErrorToast(response.error, 'showBorder')
      }

      setPressuresReports(response.pressures || [])
      setPagination(response.pagination || defaultPaginationState)
      setIsPressuresLoading(false)
    } catch (err) {
      setPressuresReports([])
      setIsPressuresLoading(false)
      setPagination(defaultPaginationState)
      localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
      setFilters(defaultFilters)
      return showErrorToast('Something went wrong', 'showBorder')
    }
  }

  const expandTable = () => {
    history(buildReportsExpandPath('pressures'))
  }

  useEffect(() => {
    if (!filters.showReports) {
      return
    }

    const runTriggered = prevRunReportTriggered.current !== isRunReportTriggered
    prevRunReportTriggered.current = isRunReportTriggered

    if (runTriggered && pagination.currentPage !== 1) {
      setPagination((prev) => {
        return { ...prev, currentPage: 1 }
      })
      return
    }

    getPressuresReports()
  }, [
    pagination.currentPage,
    pagination.resultsPerPage,
    pressuresSorting.sortBy,
    pressuresSorting.sortDirection,
    isRunReportTriggered
  ])

  const downloadCSV = async () => {
    setIsPressuresLoading(true)
    setLoadingScreenMessage('buildingReport')
    const queryString = getUrlForAllFilters(filters)

    try {
      const response = await downloadPressuresReportFile(queryString)

      if (response.error || !response.blob) {
        showErrorToast('something go wrong')
        setLoadingScreenMessage('runningReport')
        return setIsPressuresLoading(false)
      }

      const getDate = new Date().toLocaleDateString('en-GB').replaceAll('/', '_')
      saveAs(response.blob, `Sites_Pressures_report_${getDate}.xlsx`)

      setIsPressuresLoading(false)
      setLoadingScreenMessage('runningReport')
    } catch (err) {
      showErrorToast('something go wrong')
      setLoadingScreenMessage('runningReport')
      setIsPressuresLoading(false)
    }
  }

  if (params.title && pressuresReports.length !== 0) {
    return (
      <ExpandedReportsTable
        title={params.title}
        loading={isPressuresLoading}
        rows={makePressuresTabRow(pressuresReports)}
        columns={makePressuresTabColumns(
          pressuresSorting,
          setPressuresSorting,
          pressuresReports
        )}
        totalNumber={pagination.totalNumber}
        resultsPerPage={pagination.resultsPerPage}
        changeResultsPerPage={changeResultsPerPage}
        download={downloadCSV}
      />
    )
  }

  return (
    <div>
      <ReportTableWrapper>
        <ReportTableTitle>
          <FormattedMessage id='pressures' />
          <Dash>{'—'}</Dash>
          <FormattedMessage id='reports' />
        </ReportTableTitle>
        <RightButtons>
          <Button dataTest='download' height='32px' marginTop='0px' onClick={downloadCSV}>
            <Download />
            <FormattedMessage id='download' />
          </Button>
        </RightButtons>
      </ReportTableWrapper>
      <ReportsTableWrapper activeTab='siteDetails'>
        <Table
          columns={makePressuresTabColumns(
            pressuresSorting,
            setPressuresSorting,
            pressuresReports
          )}
          rows={makePressuresTabRow(pressuresReports)}
          noClearFilter={true}
          noResultMessage='noPressuresMatchFilteringCriteria'
          dataTest='pressuresTab-list'
          isLoading={isPressuresLoading}
        />
      </ReportsTableWrapper>
      <ReportsPaginationWrapper>
        <ItemsPerPageDropdown
          currentPage={pagination.currentPage}
          resultsPerPage={pagination.resultsPerPage}
          totalNumber={pagination.totalNumber}
          onChange={changeResultsPerPage}
          dataTest='pressures-items-per-page'
          menuPlacement='top'
          alternativeOptions={true}
        />
        {pagination.totalPages > 1 && (
          <Pagination
            activePages={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest='page-number'
          />
        )}
      </ReportsPaginationWrapper>
    </div>
  )
}

export default PressuresReportsTab
